/*
 * Planet Amino custom cart drawer.
 *
 * Architecture (refactored per Shopify Ajax API docs):
 *
 *   cart = last /cart.js response, treated as the source of truth. It owns
 *   the real state; the UI is a pure function of (cart, optimistic overlays).
 *
 *   Subscription state is DERIVED from cart.items — not from a parallel
 *   `_pa_sub_*` attribute. "Is the cart subscribed?" = does at least one
 *   eligible line have a selling_plan_allocation. "Interval?" = extracted
 *   from that line's plan name. Empty cart ⇒ not subscribed, period; prior
 *   session cookies can't leak stale intent into a fresh cart.
 *
 *   All writes flow through a single SERIAL task queue. While it's draining,
 *   `syncing` is true — the checkout button disables so the customer can't
 *   leave mid-flight. After every drain we re-fetch /cart.js and re-render.
 *   No convergence worker, no parallel fan-out, no race conditions.
 *
 *   Optimistic overlays (lineQtyOverride, optimisticSub) live only until the
 *   queue drains; render() layers them on top of cart for instant feedback.
 *
 *   URLs always use window.Shopify.routes.root (ends in '/') so AJAX stays
 *   locale-correct across markets — per the Shopify Ajax API guide.
 *
 *   Shopify gotcha: /cart/change.js defaults any missing field. If you send
 *   only `{id, selling_plan}`, the line's quantity resets to 1. Always send
 *   quantity alongside selling_plan.
 */

(function () {
  'use strict';

  const SUBSCRIBE_GROUP_NAME = 'Subscribe & Save';
  const ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const root = document.querySelector('[data-pa-cart-drawer]');
  if (!root) return;
  document.body.classList.add('pa-cart-using-custom');

  const config = {
    threshold: parseFloat(root.dataset.freeShippingThreshold) || 0,
    intervals: (root.dataset.intervals || '4,6,8')
      .split(',')
      .map((v) => parseInt(v.trim(), 10))
      .filter((v) => v > 0),
    defaultInterval: parseInt(root.dataset.defaultInterval, 10) || 4,
    priorityCollection: root.dataset.priorityCollection || '',
  };

  const i18n = window.paCartI18n || {};

  // ---------- State ----------
  let cart = null;
  let isOpen = false;
  let syncing = false;
  // line.key -> optimistic quantity; 0 means "treat as removed in UI"
  const lineQtyOverride = new Map();
  // { active, interval } | null — set by onSubToggle/onIntervalChange, cleared
  // when the queue drain finishes.
  let optimisticSub = null;
  // Session-only memory of the last interval the user picked in the dropdown.
  // Used when the cart has no subscribed lines to derive an interval from.
  // Not persisted — fresh page load falls back to config.defaultInterval.
  let intendedInterval = 0;
  // handle -> { [weeks]: { planId, kind, discountFraction?, discountCents?, fixedPriceCents? } } | null | Promise
  const planData = new Map();
  const productCache = new Map();
  let recommendationCache = null;
  let lastAddedProductId = null;
  // Persists the user's variant pick across re-renders so toggling sub or
  // editing a line doesn't snap the cross-sell back to the first variant.
  // Keyed on product handle: a different candidate resets the state.
  let crossSellState = { handle: null, variantId: null };
  // Set on openDrawer so closeDrawer can return focus to the launcher.
  let lastFocusedElement = null;

  const taskQueue = [];
  let queueDraining = false;
  let qtyDebounceTimer = null;
  let subDebounceTimer = null;
  // line.key -> error message; rendered inline under that line. Cleared on
  // next successful change for the same key (or on render after re-fetch).
  const lineErrors = new Map();
  // Global add-failure message; rendered above the items list. Cleared on
  // next successful add.
  let addError = null;

  // ---------- Utilities ----------
  // Formats in the visitor's active currency (snippets/pa-money.liquid).
  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return window.paFormatMoney(cents);
  }

  function t(key, vars) {
    let s = i18n[key] || '';
    if (vars) {
      Object.keys(vars).forEach((k) => {
        s = s.replace(new RegExp('\\[' + k + '\\]', 'g'), vars[k]);
      });
    }
    return s;
  }

  function $(sel, scope) {
    return (scope || root).querySelector(sel);
  }
  function $$(sel, scope) {
    return Array.from((scope || root).querySelectorAll(sel));
  }

  function url(path) {
    return ROOT + path.replace(/^\//, '');
  }

  // ---------- HTTP ----------
  async function fetchJSON(path, body, opts) {
    opts = opts || {};
    const init = {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Accept: 'application/json' },
      keepalive: true,
    };
    if (opts.method) init.method = opts.method;
    if (body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const res = await fetch(url(path), init);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      if (window.paCartDebug) {
        console.warn('[pa-cart]', path, res.status, text, 'sent=', body);
      }
      const err = new Error('cart-api-' + res.status);
      err.status = res.status;
      err.body = text;
      throw err;
    }
    return res.json();
  }

  // Cart mutations always request the cart-icon-bubble section back so the
  // header bubble stays in sync without a second /cart.js round-trip. See
  // https://shopify.dev/docs/api/ajax/reference/cart#bundled-section-rendering
  async function cartMutation(path, body) {
    const fullBody = Object.assign({}, body, { sections: 'cart-icon-bubble' });
    const data = await fetchJSON(path, fullBody);
    updateCartIconBubble(data);
    return data;
  }

  function updateCartIconBubble(data) {
    if (!data || !data.sections || !data.sections['cart-icon-bubble']) return;
    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = data.sections['cart-icon-bubble'];
    const sectionContent = wrapper.querySelector('#shopify-section-cart-icon-bubble');
    if (sectionContent) target.innerHTML = sectionContent.innerHTML;
  }

  async function getCart() {
    cart = await fetchJSON('cart.js');
    await enrichCart();
    return cart;
  }

  async function enrichCart() {
    if (!cart || !cart.items) return;
    await Promise.all(
      cart.items.map((line) => getPlanData(line.handle).catch(() => null))
    );
  }

  // ---------- Plan data ----------
  function parseIntervalFromSellingPlan(plan) {
    if (!plan) return null;
    const haystack = [plan.name, ...(plan.options || []).map((o) => (o && o.value) || o)].join(
      ' '
    );
    const m = haystack.match(/(\d+)\s*(week|viikko|wochen)/i);
    if (m) return parseInt(m[1], 10);
    for (const opt of plan.options || []) {
      const v = typeof opt === 'string' ? opt : opt.value;
      if (v && /^\d+$/.test(v.trim())) return parseInt(v.trim(), 10);
    }
    return null;
  }

  function extractAdjustment(plan) {
    const adj = plan && plan.price_adjustments && plan.price_adjustments[0];
    if (!adj) return { kind: 'none' };
    if (adj.value_type === 'percentage')
      return { kind: 'percentage', discountFraction: Number(adj.value) / 100 };
    if (adj.value_type === 'fixed_amount')
      return { kind: 'fixed_amount', discountCents: Number(adj.value) };
    if (adj.value_type === 'price')
      return { kind: 'price', fixedPriceCents: Number(adj.value) };
    return { kind: 'none' };
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    const p = fetch(url('products/' + handle + '.js'))
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    productCache.set(handle, p);
    return p;
  }

  async function getPlanData(handle) {
    if (planData.has(handle)) {
      const cached = planData.get(handle);
      return cached && typeof cached.then === 'function' ? cached : cached;
    }
    const promise = (async () => {
      const product = await fetchProduct(handle);
      if (!product || !product.selling_plan_groups || product.selling_plan_groups.length === 0) {
        return null;
      }
      const group =
        product.selling_plan_groups.find((g) => (g.name || '').trim() === SUBSCRIBE_GROUP_NAME) ||
        product.selling_plan_groups[0];
      if (!group || !group.selling_plans) return null;
      const map = {};
      for (const plan of group.selling_plans) {
        const weeks = parseIntervalFromSellingPlan(plan);
        if (!weeks) continue;
        map[weeks] = { planId: plan.id, ...extractAdjustment(plan) };
      }
      return Object.keys(map).length ? map : null;
    })();
    planData.set(handle, promise);
    promise
      .then((val) => planData.set(handle, val))
      .catch(() => planData.set(handle, null));
    return promise;
  }

  function planEntry(line, weeks) {
    const map = planData.get(line.handle);
    if (!map || typeof map.then === 'function') return null;
    return map[weeks] || null;
  }

  function isEligible(line) {
    const map = planData.get(line.handle);
    if (!map || typeof map.then === 'function') return false;
    return Object.keys(map).length > 0;
  }

  // ---------- Subscription state ----------
  // The drawer's "is the cart subscribed?" answer is DERIVED from the cart's
  // actual lines, never from a parallel attribute. Empty cart ⇒ active:false,
  // regardless of any stale `_pa_sub_*` attribute left over in the session
  // cookie from a previous sitting. Interval follows the first subscribed
  // line so the dropdown always reflects reality.
  function getSub() {
    if (optimisticSub) return optimisticSub;
    const fallbackInterval = intendedInterval || config.defaultInterval;
    if (!cart || !cart.items || cart.items.length === 0) {
      return { active: false, interval: fallbackInterval };
    }
    const subbed = cart.items.find(
      (l) =>
        isEligible(l) &&
        l.selling_plan_allocation &&
        l.selling_plan_allocation.selling_plan
    );
    if (subbed) {
      const weeks = parseIntervalFromSellingPlan(
        subbed.selling_plan_allocation.selling_plan
      );
      return { active: true, interval: weeks || fallbackInterval };
    }
    return { active: false, interval: fallbackInterval };
  }

  // ---------- Serial task queue ----------
  // Everything that mutates the server cart goes through here. Serial so we
  // never race on the same session's cart. Checkout button disables while
  // draining so the customer can't leave mid-flight.
  async function enqueue(task) {
    taskQueue.push(task);
    if (queueDraining) return;
    queueDraining = true;
    syncing = true;
    render();
    try {
      while (taskQueue.length > 0) {
        const fn = taskQueue.shift();
        try {
          await fn();
        } catch (e) {
          if (window.paCartDebug) console.warn('[pa-cart] task failed', e);
        }
      }
      try {
        await getCart();
      } catch (_) {}
    } finally {
      queueDraining = false;
      syncing = false;
      // Don't clear optimisticSub here — the drain may have processed only a
      // qty change, leaving the user's pending sub toggle still in flight.
      // flushSubChange clears it itself after the sub task actually runs.
      cleanupLineOverrides();
      render();
    }
  }

  function cleanupLineOverrides() {
    if (!cart || !cart.items) return;
    const validKeys = new Set(cart.items.map((l) => l.key));
    for (const key of Array.from(lineQtyOverride.keys())) {
      const line = cart.items.find((l) => l.key === key);
      if (!line) {
        lineQtyOverride.delete(key);
      } else if (line.quantity === lineQtyOverride.get(key)) {
        lineQtyOverride.delete(key);
      }
    }
    // Drop errors for lines that no longer exist in the cart.
    for (const key of Array.from(lineErrors.keys())) {
      if (!validKeys.has(key)) lineErrors.delete(key);
    }
  }

  // ---------- Actions ----------
  function requestLineQty(key, qty, immediate) {
    lineErrors.delete(key);
    lineQtyOverride.set(key, Math.max(0, qty));
    render();
    clearTimeout(qtyDebounceTimer);
    if (immediate) {
      qtyDebounceTimer = null;
      flushLineQtyChanges();
    } else {
      qtyDebounceTimer = setTimeout(() => {
        qtyDebounceTimer = null;
        flushLineQtyChanges();
      }, 200);
    }
  }

  function flushLineQtyChanges() {
    if (!cart || !cart.items) return;
    const toChange = [];
    // Drop overrides that are now no-ops (cart already at target qty, or the
    // line is gone). Without this, a quick `+` then `-` leaves a stale override
    // in the map, which would keep checkout disabled forever via hasPendingWork.
    for (const [key, qty] of Array.from(lineQtyOverride.entries())) {
      const line = cart.items.find((l) => l.key === key);
      if (!line) {
        lineQtyOverride.delete(key);
        continue;
      }
      if (line.quantity === qty) {
        lineQtyOverride.delete(key);
        continue;
      }
      toChange.push(key);
    }
    if (toChange.length === 0) {
      // Pending state changed (override map shrank); refresh checkout button.
      render();
      return;
    }
    enqueue(async () => {
      for (const key of toChange) {
        const finalQty = lineQtyOverride.has(key) ? lineQtyOverride.get(key) : null;
        if (finalQty == null) continue;
        let succeeded = false;
        try {
          await cartMutation('cart/change.js', { id: key, quantity: finalQty });
          succeeded = true;
          lineErrors.delete(key);
        } catch (_) {
          lineErrors.set(key, t('lineUpdateFailed') || 'Could not update.');
        }
        // Failed mutation (out of stock, etc.) — drop the optimistic override
        // so the next render snaps back to whatever the server actually has.
        if (!succeeded) lineQtyOverride.delete(key);
      }
    });
  }

  function requestSub(active, interval) {
    optimisticSub = { active, interval };
    render();
    clearTimeout(subDebounceTimer);
    subDebounceTimer = setTimeout(() => {
      subDebounceTimer = null;
      flushSubChange();
    }, 200);
  }

  function flushSubChange() {
    const target = optimisticSub;
    if (!target) return;
    // Remember the interval the user landed on, so the dropdown can show it
    // next time cart has no subscribed line to derive from.
    intendedInterval = target.interval;
    enqueue(async () => {
      // Re-read cart so we have current line keys + allocations (a concurrent
      // add could have happened between the toggle and the queue drain).
      try {
        cart = await fetchJSON('cart.js');
        await enrichCart();
      } catch (_) {
        return;
      }
      // Align each eligible line's selling_plan. Ineligible lines (t-shirts,
      // custom-box bundles without a plan group) stay one-time by design.
      //
      // Two Shopify-doc gotchas baked in here:
      //   1) selling_plan changes must identify the line by `line` (1-based
      //      index), not `id`. Per the Cart Ajax API reference.
      //   2) Always include quantity. Missing quantity defaults to 1, which
      //      would silently reset a 10-cookie line.
      for (let i = 0; i < cart.items.length; i++) {
        const line = cart.items[i];
        if (!isEligible(line)) continue;
        const map = planData.get(line.handle);
        const entry = map[target.interval];
        const wantPlan = target.active && entry ? entry.planId : null;
        const havePlan =
          (line.selling_plan_allocation &&
            line.selling_plan_allocation.selling_plan &&
            line.selling_plan_allocation.selling_plan.id) ||
          null;
        if (wantPlan === havePlan) continue;
        try {
          await cartMutation('cart/change.js', {
            line: i + 1,
            quantity: line.quantity,
            selling_plan: wantPlan || '',
          });
        } catch (_) {}
      }
      // Sub intent has been applied to the server (or no work was needed).
      // Clearing here — not in the queue's finally — protects against the
      // case where a qty drain finishes between toggle click and sub flush.
      if (optimisticSub === target) optimisticSub = null;
    });
  }

  async function addToCart(variantId, quantity, meta) {
    const handle = meta && meta.handle;
    if (handle) await getPlanData(handle).catch(() => null);
    const sub = getSub();
    const map = handle ? planData.get(handle) : null;
    const entry =
      map && typeof map.then !== 'function' && sub.active ? map[sub.interval] : null;
    const body = { id: variantId, quantity };
    if (entry) body.selling_plan = entry.planId;
    if (meta && meta.product_id) lastAddedProductId = meta.product_id;
    // Intentionally NOT clearing recommendationCache here. Shopify's
    // recommendations endpoint excludes products already in the cart, so
    // a refetch would drop the just-added multi-variant product — making it
    // impossible for the customer to add a second flavor of the same item
    // (e.g. Vanilla after Chocolate). The cache is seeded once when the
    // drawer first renders cross-sell, then persists for the session.
    enqueueAdd(async () => cartMutation('cart/add.js', body));
    openDrawer();
  }

  // Submits a product form to /cart/add.js with the form's full FormData
  // intact. Preserves properties[*], original selling_plan, gift-card recipient
  // fields, etc. Layers in the cart's selling_plan only if the form didn't
  // specify one and the cart is currently subscribed.
  async function addFormToCart(form) {
    const fd = new FormData(form);
    // Multi-item forms (items[0][id]=...) are not single-add — let them through
    // to whatever handler owns them.
    for (const key of fd.keys()) {
      if (/^items\[/.test(key)) return false;
    }
    const variantId = fd.get('id');
    if (!variantId) return false;

    let handle = form.dataset.productHandle;
    if (!handle) {
      const parent = form.closest('[data-product-handle]');
      if (parent) handle = parent.dataset.productHandle;
    }
    if (!handle) {
      const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (m) handle = m[1];
    }

    if (!fd.get('selling_plan') && handle) {
      try { await getPlanData(handle); } catch (_) {}
      const sub = getSub();
      const map = planData.get(handle);
      if (sub.active && map && typeof map.then !== 'function') {
        const planEntry = map[sub.interval];
        if (planEntry) fd.set('selling_plan', planEntry.planId);
      }
    }

    if (form.dataset.productId) {
      const pid = parseInt(form.dataset.productId, 10);
      if (pid) lastAddedProductId = pid;
    }

    fd.append('sections', 'cart-icon-bubble');
    enqueueAdd(async () => {
      const res = await fetch(url('cart/add.js'), {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const err = new Error('cart-add-' + res.status);
        err.status = res.status;
        throw err;
      }
      const data = await res.json();
      updateCartIconBubble(data);
      return data;
    });
    openDrawer();
    return true;
  }

  // Wraps an add task with success/failure tracking so we can surface a
  // banner if /cart/add.js rejects.
  function enqueueAdd(task) {
    enqueue(async () => {
      try {
        await task();
        addError = null;
      } catch (_) {
        addError = t('addFailed') || 'Could not add to cart.';
      }
    });
  }

  // ---------- Optimistic pricing ----------
  // Projection: layer the user's current sub intent on top of server prices
  // so the UI doesn't flicker when toggling. Falls back to server truth when
  // the math would get too speculative (non-percentage plans, etc.).
  function computeLinePrice(line) {
    const override = lineQtyOverride.get(line.key);
    const qty = override != null ? override : line.quantity;

    const serverUnit =
      typeof line.final_price === 'number' ? line.final_price : line.price;
    const allocCompare =
      line.selling_plan_allocation && line.selling_plan_allocation.compare_at_price;
    const catalog = allocCompare || line.original_price || line.price;

    const sub = getSub();
    const entry = planEntry(line, sub.interval);
    const serverHasSub = !!(
      line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
    );
    const wantSub = sub.active && !!entry;
    const subPct = entry && entry.kind === 'percentage' ? entry.discountFraction : null;

    let unit = serverUnit;
    if (wantSub === serverHasSub) {
      // Already in sync.
    } else if (wantSub && subPct != null) {
      unit = Math.round(serverUnit * (1 - subPct));
    } else if (!wantSub && serverHasSub && subPct != null) {
      unit = Math.round(serverUnit / (1 - subPct));
    } else if (wantSub && entry && entry.kind === 'fixed_amount') {
      unit = Math.max(0, serverUnit - entry.discountCents);
    } else if (!wantSub && serverHasSub && entry && entry.kind === 'fixed_amount') {
      unit = serverUnit + entry.discountCents;
    }

    return {
      unit,
      line: unit * qty,
      lineCompareAt: catalog * qty,
    };
  }

  function computeTotals() {
    if (!cart || !cart.items) return { total: 0 };
    let total = 0;
    for (const line of cart.items) {
      const override = lineQtyOverride.get(line.key);
      if (override === 0) continue; // optimistically removed
      const p = computeLinePrice(line);
      total += p.line;
    }
    return { total };
  }

  function effectiveItemCount() {
    if (!cart) return 0;
    let count = 0;
    for (const line of cart.items) {
      const override = lineQtyOverride.get(line.key);
      count += override != null ? override : line.quantity;
    }
    return count;
  }

  // ---------- Render ----------
  function render() {
    if (!cart) return;
    const sub = getSub();
    const { total } = computeTotals();
    const empty = effectiveItemCount() === 0;

    $('[data-pa-empty]').hidden = !empty;
    $('[data-pa-items]').hidden = empty;
    $('[data-pa-footer]').hidden = empty;

    renderShipping(total);

    const anyEligible = cart.items.some((l) => isEligible(l));
    const subEl = $('[data-pa-subscription]');
    subEl.hidden = !anyEligible || empty;
    if (anyEligible) {
      $('[data-pa-sub-toggle]').checked = sub.active;
      renderIntervalSelect(sub.interval, sub.active);
    }

    renderLines();
    renderCrossSell();

    const errEl = $('[data-pa-add-error]');
    if (errEl) {
      if (addError) {
        errEl.textContent = addError;
        errEl.hidden = false;
      } else {
        errEl.textContent = '';
        errEl.hidden = true;
      }
    }

    const checkout = $('[data-pa-checkout]');
    const isSyncingNow = syncing && !empty;
    // Pending = optimistic state not yet reconciled with the server. Covers
    // both the 200ms debounce window before flushLineQtyChanges runs AND the
    // gap where a sub toggle is awaiting its queue task. Keeping checkout
    // disabled across this window prevents the customer from leaving with
    // the previous server-side cart state.
    const isPending =
      !empty && (lineQtyOverride.size > 0 || optimisticSub != null);
    checkout.disabled = empty || isSyncingNow || isPending;
    checkout.classList.toggle('pa-cart-drawer__checkout--syncing', isSyncingNow || isPending);
    $('[data-pa-checkout-total]').textContent = empty ? '' : formatMoney(total);

    document.dispatchEvent(new CustomEvent('pa-cart:updated', { detail: { cart } }));
  }

  function renderShipping(total) {
    // Threshold is configured in EUR; cart totals are in the active currency.
    const threshold = window.paMoney.fromShopCurrency(config.threshold * 100);
    const textEl = $('[data-pa-shipping-text]');
    const barEl = $('[data-pa-shipping-bar]');
    const truckEl = $('[data-pa-shipping-truck]');
    if (!threshold || threshold <= 0) {
      $('[data-pa-shipping]').hidden = true;
      return;
    }
    const pct = Math.min(100, Math.round((total / threshold) * 100));
    if (barEl) barEl.style.setProperty('--pa-shipping-pct', pct + '%');
    if (truckEl) truckEl.style.left = pct + '%';
    if (total >= threshold) {
      textEl.innerHTML = `<strong>${t('freeShippingUnlocked')} 🎉</strong>`;
    } else {
      const remaining = formatMoney(threshold - total);
      textEl.innerHTML = t('freeShippingProgress', {
        amount: `<strong>${remaining}</strong>`,
      });
    }
  }

  function renderIntervalSelect(current, active) {
    const select = $('[data-pa-sub-interval]');
    select.disabled = !active;
    if (select.dataset.built === '1') {
      select.value = String(current);
      return;
    }
    select.innerHTML = '';
    config.intervals.forEach((weeks) => {
      const opt = document.createElement('option');
      opt.value = String(weeks);
      opt.textContent = t('intervalEveryWeeks', { count: weeks });
      select.appendChild(opt);
    });
    select.value = String(current);
    select.dataset.built = '1';
  }

  function renderLines() {
    const list = $('[data-pa-items]');
    const tpl = document.getElementById('pa-cart-line-template');
    list.innerHTML = '';
    cart.items.forEach((line) => {
      const override = lineQtyOverride.get(line.key);
      if (override === 0) return; // hidden optimistically
      const displayQty = override != null ? override : line.quantity;

      const node = tpl.content.firstElementChild.cloneNode(true);
      node.dataset.lineKey = line.key;

      const img = $('[data-pa-line-image]', node);
      if (line.image) {
        img.src = line.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x160$1$2');
        img.alt = line.product_title;
      }

      $$('[data-pa-line-url]', node).forEach((el) => {
        el.href = line.url;
      });

      const hasRealVariant =
        line.variant_title && line.variant_title.toLowerCase() !== 'default title';
      $('[data-pa-line-title]', node).textContent = hasRealVariant
        ? `${line.product_title} ${line.variant_title}`
        : line.product_title;

      const optEl = $('[data-pa-line-option]', node);
      if (optEl) optEl.hidden = true;

      // Subscription badge — driven by intent + plan availability.
      const sub = getSub();
      const subEl = $('[data-pa-line-subscription]', node);
      const entry = planEntry(line, sub.interval);
      if (sub.active && entry) {
        subEl.textContent = t('subscriptionLine', { count: sub.interval });
        subEl.hidden = false;
      }

      const lineErrEl = $('[data-pa-line-error]', node);
      if (lineErrEl) {
        const msg = lineErrors.get(line.key);
        if (msg) {
          lineErrEl.textContent = msg;
          lineErrEl.hidden = false;
        } else {
          lineErrEl.hidden = true;
        }
      }

      const qtyInput = $('[data-pa-line-qty-input]', node);
      qtyInput.value = displayQty;
      qtyInput.addEventListener('change', () => {
        const q = Math.max(0, parseInt(qtyInput.value, 10) || 0);
        requestLineQty(line.key, q, false);
      });
      $('[data-pa-line-increase]', node).addEventListener('click', () => {
        const current = override != null ? override : line.quantity;
        requestLineQty(line.key, current + 1, false);
      });
      $('[data-pa-line-decrease]', node).addEventListener('click', () => {
        const current = override != null ? override : line.quantity;
        requestLineQty(line.key, Math.max(0, current - 1), false);
      });
      $('[data-pa-line-remove]', node).addEventListener('click', () => {
        requestLineQty(line.key, 0, true);
      });

      const priceEl = $('[data-pa-line-price]', node);
      const p = computeLinePrice(line);
      if (p.lineCompareAt && p.lineCompareAt > p.line) {
        priceEl.innerHTML =
          `<span class="pa-compare">${formatMoney(p.lineCompareAt)}</span>` +
          formatMoney(p.line);
      } else {
        priceEl.textContent = formatMoney(p.line);
      }

      list.appendChild(node);
    });
  }

  // ---------- Cross-sell ----------
  async function fetchRecommendations() {
    const results = [];
    if (config.priorityCollection) {
      try {
        const r = await fetch(
          url('collections/' + config.priorityCollection + '/products.json?limit=20')
        );
        if (r.ok) {
          const data = await r.json();
          if (data.products) results.push(...data.products);
        }
      } catch (_) {}
    }
    const seedProductId =
      lastAddedProductId || (cart.items[0] && cart.items[0].product_id);
    if (seedProductId) {
      try {
        const r = await fetch(
          url(
            'recommendations/products.json?product_id=' +
              seedProductId +
              '&limit=8&intent=complementary'
          )
        );
        if (r.ok) {
          const data = await r.json();
          if (data.products) results.push(...data.products);
        }
      } catch (_) {}
    }
    return applyRestockHolds(results);
  }

  // Variants taken off sale with a restock date (snippets/restock-note) still
  // say available in Shopify's JSON, so mark them sold out here.
  function applyRestockHolds(products) {
    const held = new Set((window.paRestockVariantIds || []).map(String));
    if (!held.size) return products;
    return products.map((p) => {
      if (!p || !p.variants) return p;
      const variants = p.variants.map((v) =>
        v && held.has(String(v.id)) ? { ...v, available: false } : v
      );
      const anyAvailable = variants.some((v) => v && v.available !== false);
      return { ...p, variants, available: p.available !== false && anyAvailable };
    });
  }

  function isConfigurableProduct(p) {
    if (p.tags && Array.isArray(p.tags)) {
      if (
        p.tags.some((tag) =>
          /cart-cross-sell[-:]link-only|custom[-.]bundle|custom[-.]box/i.test(tag)
        )
      )
        return true;
    }
    if (p.handle && /custom-box|kaariboksi/i.test(p.handle)) return true;
    return false;
  }

  function findSellingPlanIdForInterval(product, weeks) {
    for (const g of product.selling_plan_groups || []) {
      for (const p of g.selling_plans || []) {
        if (parseIntervalFromSellingPlan(p) === weeks) return p.id;
      }
    }
    return null;
  }

  function findAllocPrice(variant, planId) {
    if (!planId) return null;
    const a = (variant.selling_plan_allocations || []).find(
      (x) => x.selling_plan_id === planId
    );
    return a ? a.per_delivery_price || a.price : null;
  }

  function variantPriceCents(variant) {
    if (typeof variant.price === 'number') return variant.price;
    const n = parseFloat(variant.price);
    return Math.round(n * 100);
  }

  // Maximum number of cross-sell cards to render in the horizontal strip.
  const CROSS_SELL_MAX = 8;

  async function renderCrossSell() {
    const container = $('[data-pa-cross-sell]');
    const cardHost = $('[data-pa-cross-sell-card]');
    if (effectiveItemCount() === 0) {
      container.hidden = true;
      return;
    }
    if (!recommendationCache) {
      recommendationCache = await fetchRecommendations();
    }
    // Build the in-cart set keyed on product_id. Used to suppress only
    // single-variant products that are already in the cart — multi-variant
    // products stay because the customer may want to add a different flavor
    // (e.g. chocolate AND vanilla protein powder).
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const seen = new Set();
    const candidates = [];
    for (const p of recommendationCache) {
      if (!p || !p.id || p.available === false) continue;
      if (seen.has(p.id)) continue;
      const variants = (p.variants || []).filter((v) => v);
      const isMulti = variants.length > 1 && !isConfigurableProduct(p);
      // Single-variant in cart: nothing more to add via cross-sell.
      if (!isMulti && inCart.has(p.id)) continue;
      seen.add(p.id);
      candidates.push(p);
      if (candidates.length >= CROSS_SELL_MAX) break;
    }
    if (candidates.length === 0) {
      container.hidden = true;
      return;
    }
    container.hidden = false;
    cardHost.innerHTML = '';
    for (const product of candidates) {
      renderCrossSellCard(cardHost, product);
    }
    setupCrossSellNav(cardHost);
  }

  // Carousel-style left/right navigation across cross-sell cards. Cards are
  // full-width with mandatory snap-x, so each click moves exactly one card.
  function setupCrossSellNav(cardHost) {
    const navWrap = $('[data-pa-cross-sell-nav]');
    const prev = $('[data-pa-cross-sell-prev]');
    const next = $('[data-pa-cross-sell-next]');
    if (!navWrap || !prev || !next) return;
    const cards = cardHost.querySelectorAll('.pa-cross-sell');
    // Single card or none → no navigation needed.
    if (cards.length <= 1) {
      navWrap.hidden = true;
      return;
    }
    navWrap.hidden = false;
    const updateButtons = () => {
      const max = cardHost.scrollWidth - cardHost.clientWidth;
      prev.disabled = cardHost.scrollLeft <= 1;
      next.disabled = cardHost.scrollLeft >= max - 1;
    };
    updateButtons();
    // Wire once per render. Scroll events catch user touch-drag scrolling
    // so the disabled state stays in sync with native scroll too.
    if (!cardHost.dataset.paNavBound) {
      cardHost.addEventListener('scroll', updateButtons, { passive: true });
      cardHost.dataset.paNavBound = '1';
    }
    // Note: `behavior: 'smooth'` here is silently ignored by Chrome when the
    // host has scroll-snap-type set, so we skip it. Snap-x proximity still
    // gives a clean snap to the next card edge.
    // updateButtons is also called explicitly because programmatic scrollBy()
    // doesn't always fire a scroll event quickly enough to update the
    // disabled state before the user clicks again.
    prev.onclick = () => {
      cardHost.scrollBy({ left: -cardHost.clientWidth });
      updateButtons();
    };
    next.onclick = () => {
      cardHost.scrollBy({ left: cardHost.clientWidth });
      updateButtons();
    };
  }

  function renderCrossSellCard(host, product) {
    const tpl = document.getElementById('pa-cart-cross-sell-template');
    const node = tpl.content.firstElementChild.cloneNode(true);
    // Use the locale-aware route root so links go to /fi/products/... on the
    // FI storefront, /de/products/... on DE, etc. The recommendations API
    // already returns the localized handle, so prefix-only is enough.
    const pdpUrl = url('products/' + product.handle);
    $$('[data-pa-cross-sell-link]', node).forEach((el) => el.setAttribute('href', pdpUrl));

    const img = $('[data-pa-cross-sell-image]', node);
    const imageFromSrc = (src) => (src || '').replace(/(\.[a-z]+)(\?|$)/i, '_200x200$1$2');
    const productFeatured =
      product.featured_image || product.image || (product.images && product.images[0]);
    const productFeaturedSrc = productFeatured
      ? typeof productFeatured === 'string'
        ? productFeatured
        : productFeatured.src
      : '';
    if (productFeaturedSrc) {
      img.src = imageFromSrc(productFeaturedSrc);
      img.alt = product.title;
    }

    $('[data-pa-cross-sell-title]', node).textContent = product.title;

    const variants = (product.variants || []).filter((v) => v);
    const configurable = isConfigurableProduct(product);
    const multiVariant = !configurable && variants.length > 1;
    const cta = $('[data-pa-cross-sell-cta]', node);
    const priceEl = $('[data-pa-cross-sell-price]', node);
    const noteEl = $('[data-pa-cross-sell-price-note]', node);
    const selectEl = $('[data-pa-cross-sell-variant]', node);

    if (multiVariant && selectEl) {
      variants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title || v.name || '';
        if (v.available === false) {
          opt.disabled = true;
          opt.textContent += ' (—)';
        }
        selectEl.appendChild(opt);
      });
      selectEl.hidden = false;
    }

    const sub = getSub();
    const planId = sub.active ? findSellingPlanIdForInterval(product, sub.interval) : null;

    let currentVariant = variants[0] || {};
    const firstAvailable = variants.find((v) => v.available !== false);
    if (firstAvailable) currentVariant = firstAvailable;
    // Restore the user's previous pick if we're re-rendering the same product.
    if (crossSellState.handle === product.handle && crossSellState.variantId) {
      const saved = variants.find(
        (v) => String(v.id) === String(crossSellState.variantId) && v.available !== false
      );
      if (saved) currentVariant = saved;
    }
    if (selectEl && multiVariant) selectEl.value = String(currentVariant.id);

    const variantImageSrc = (variant) => {
      if (!variant) return '';
      if (variant.featured_image) {
        return typeof variant.featured_image === 'string'
          ? variant.featured_image
          : variant.featured_image.src || '';
      }
      if (variant.image) {
        return typeof variant.image === 'string' ? variant.image : variant.image.src || '';
      }
      return '';
    };

    const update = (variant) => {
      const vImg = variantImageSrc(variant);
      if (vImg) img.src = imageFromSrc(vImg);
      else if (productFeaturedSrc) img.src = imageFromSrc(productFeaturedSrc);

      const subPrice = findAllocPrice(variant, planId);
      const subApplicable = subPrice != null;
      const catalogCents = variantPriceCents(variant);
      const displayCents = subApplicable ? subPrice : catalogCents;
      if (catalogCents > displayCents) {
        priceEl.innerHTML =
          `<span class="pa-compare">${formatMoney(catalogCents)}</span>` +
          formatMoney(displayCents);
      } else {
        priceEl.textContent = formatMoney(displayCents);
      }
      if (noteEl) noteEl.hidden = true;
      if (configurable) {
        cta.textContent = t('chooseFlavors');
      } else {
        cta.textContent = subApplicable ? t('addToSubscription') : t('addToCart');
      }
      cta.classList.toggle('pa-cross-sell__cta--sub', subApplicable);
    };

    update(currentVariant);

    if (multiVariant && selectEl) {
      const swallow = (e) => e.stopPropagation();
      selectEl.addEventListener('mousedown', swallow);
      selectEl.addEventListener('click', swallow);
      selectEl.addEventListener('change', (e) => {
        e.stopPropagation();
        const v = variants.find((vv) => String(vv.id) === e.target.value);
        if (v) {
          currentVariant = v;
          crossSellState = { handle: product.handle, variantId: v.id };
          update(v);
        }
      });
    }

    if (configurable) {
      cta.addEventListener('click', (e) => {
        e.stopPropagation();
        window.location.href = pdpUrl;
      });
    } else {
      cta.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        cta.disabled = true;
        try {
          await addToCart(currentVariant.id, 1, {
            product_id: product.id,
            handle: product.handle,
          });
        } finally {
          cta.disabled = false;
        }
      });
    }

    // Caller (renderCrossSell) owns clearing the host so multiple cards
    // can be appended into the horizontal scroller.
    host.appendChild(node);
  }

  // ---------- Open/close ----------
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusableInDrawer() {
    const panel = root.querySelector('.pa-cart-drawer__panel');
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hidden && el.offsetParent !== null
    );
  }

  function openDrawer() {
    isOpen = true;
    lastFocusedElement = document.activeElement;
    root.dataset.paOpen = 'true';
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pa-cart-open');
    // Move focus into the drawer so keyboard users land inside it.
    setTimeout(() => {
      const closeBtn = root.querySelector('.pa-cart-drawer__close');
      if (closeBtn) closeBtn.focus();
    }, 0);
  }

  function closeDrawer() {
    isOpen = false;
    root.dataset.paOpen = 'false';
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pa-cart-open');
    // Return focus to the element that opened the drawer (cart icon, etc.).
    if (lastFocusedElement && document.contains(lastFocusedElement)) {
      try { lastFocusedElement.focus(); } catch (_) {}
    }
    lastFocusedElement = null;
  }

  // ---------- Event wiring ----------
  function bindEvents() {
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-pa-cart-close]')) closeDrawer();
    });

    // Click-outside-to-close. Independent of the overlay element, which can be
    // hidden by third-party stylesheets (UpCart). Uses pointerdown so the
    // drawer-opening click on the cart icon doesn't immediately re-close it.
    document.addEventListener('pointerdown', (e) => {
      if (!isOpen) return;
      if (e.target.closest('.pa-cart-drawer__panel')) return;
      closeDrawer();
    });

    document.addEventListener('keydown', (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (e.key === 'Tab') {
        const focusables = getFocusableInDrawer();
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        // If focus is outside the drawer (e.g. browser swallowed it), pull it in.
        if (!root.contains(active)) {
          e.preventDefault();
          first.focus();
          return;
        }
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    $('[data-pa-sub-toggle]').addEventListener('change', (e) => {
      const sub = getSub();
      requestSub(e.target.checked, sub.interval);
    });
    $('[data-pa-sub-interval]').addEventListener('change', (e) => {
      const sub = getSub();
      requestSub(sub.active, parseInt(e.target.value, 10));
    });

    const howToggle = $('[data-pa-how-toggle]');
    const howInfo = $('[data-pa-how-info]');
    if (howToggle && howInfo) {
      howToggle.addEventListener('click', () => {
        const open = howToggle.getAttribute('aria-expanded') === 'true';
        howToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        howInfo.hidden = open;
      });
    }

    $('[data-pa-checkout]').addEventListener('click', (e) => {
      if (syncing) {
        e.preventDefault();
        return;
      }
      window.location.href = url('checkout');
    });

    // Intercept cart-icon clicks so the drawer opens instead of navigating.
    document.addEventListener(
      'click',
      async (e) => {
        const cartLink = e.target.closest(
          'a[href$="/cart"], a[href$="/cart/"], a[href*="/cart?"], #cart-icon-bubble, [data-pa-cart-open]'
        );
        if (cartLink && !cartLink.closest('[data-pa-cart-drawer]')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          try {
            await getCart();
          } catch (_) {}
          render();
          openDrawer();
        }
      },
      true
    );

    // Intercept Dawn-style single-item product form submits, forwarding the
    // full FormData so properties[*], original selling_plan, and other line
    // item fields survive. Multi-item forms (items[n][...]) and forms without
    // an `id` field are not intercepted — their own handlers run normally.
    document.addEventListener(
      'submit',
      async (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = form.getAttribute('action') || '';
        if (!/\/cart\/add/.test(action)) return;
        // Probe the form first; only preventDefault if we'll actually handle it.
        const fd = new FormData(form);
        let isMultiItem = false;
        for (const key of fd.keys()) {
          if (/^items\[/.test(key)) { isMultiItem = true; break; }
        }
        if (isMultiItem || !fd.get('id')) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        await addFormToCart(form);
      },
      true
    );

    // Other JS on the site (bundle builders, variant selectors) signals cart
    // changes through cart:update. We re-fetch and re-render; we do NOT try
    // to reconcile sub state here — the bundle builder is expected to call
    // window.paCart.getCartSubState() before its add.
    const onExternalUpdate = async () => {
      recommendationCache = null;
      try {
        await getCart();
      } catch (_) {}
      render();
      openDrawer();
    };

    document.addEventListener('cart:update', onExternalUpdate);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, onExternalUpdate);
      } catch (_) {}
    }
  }

  // ---------- Init ----------
  async function init() {
    bindEvents();
    try {
      await getCart();
    } catch (_) {
      return;
    }
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------- Public API ----------
  // Other JS (PDP bundle builders, etc.) reads getCartSubState to align adds
  // with the cart's current cadence instead of the PDP's own widget.
  window.paCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: async () => {
      try {
        await getCart();
      } catch (_) {}
      render();
    },
    addToCart,
    getCartSubState: () => {
      const s = getSub();
      return { active: s.active, interval: s.interval };
    },
  };
})();
